import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ArrowRight } from "lucide-react";
import api, { assetUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [calc, setCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (items.length === 0) {
      setCalc(null);
      return;
    }
    setCalcLoading(true);
    setCalcError(null);
    api
      .post("/checkout/calculate", {
        items: items.map((i) => ({
          product_id: i.product_id,
          size: i.size,
          quantity: i.quantity,
        })),
      })
      .then((r) => setCalc(r.data))
      .catch((e) => {
        setCalcError(e.response?.data?.detail || "Erreur de calcul");
        setCalc(null);
      })
      .finally(() => setCalcLoading(false));
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display text-4xl mb-4">Ton panier est vide</h1>
        <p className="text-white/50 mb-8">
          File en boutique chercher le merch qui te correspond.
        </p>
        <Link to="/" className="btn-fluo" data-testid="empty-cart-shop">
          Voir la boutique
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 lg:py-16">
      <h1 className="font-display text-4xl md:text-5xl mb-10">Panier</h1>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div
              key={`${it.product_id}-${it.size}`}
              className="fluo-card flex gap-4 p-4 items-center"
              data-testid={`cart-item-${it.product_id}-${it.size}`}
            >
              <Link to={`/product/${it.product_id}`} className="shrink-0">
                {it.photo ? (
                  <img
                    src={assetUrl(it.photo)}
                    alt=""
                    className="w-24 h-24 object-contain bg-black border border-white/10"
                  />
                ) : (
                  <div className="w-24 h-24 bg-black border border-white/10" />
                )}
              </Link>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg leading-tight">{it.name}</div>
                <div className="text-xs uppercase tracking-widest text-white/40 mt-1">
                  Taille {it.size}
                </div>
                <div className="text-magenta font-display mt-1">
                  {(it.unit_price * it.quantity).toFixed(2)} CHF
                </div>
              </div>
              <div className="flex items-center gap-2 border border-white/10">
                <button
                  onClick={() => updateQty(it.product_id, it.size, it.quantity - 1)}
                  disabled={it.quantity <= 1}
                  className="p-2 hover:text-magenta disabled:opacity-30"
                  data-testid={`qty-minus-${it.product_id}-${it.size}`}
                >
                  <Minus size={14} />
                </button>
                <span className="w-8 text-center text-sm">{it.quantity}</span>
                <button
                  onClick={() => updateQty(it.product_id, it.size, it.quantity + 1)}
                  className="p-2 hover:text-magenta"
                  data-testid={`qty-plus-${it.product_id}-${it.size}`}
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={() => removeItem(it.product_id, it.size)}
                className="p-2 text-white/40 hover:text-magenta"
                data-testid={`remove-${it.product_id}-${it.size}`}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>

        <aside className="fluo-card p-6 h-fit sticky top-24" data-testid="cart-summary">
          <h2 className="font-display text-2xl mb-4">Récapitulatif</h2>

          {calcError && (
            <div className="text-magenta text-sm mb-3" data-testid="cart-error">
              {calcError}
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-white/60">Sous-total</span>
              <span data-testid="cart-subtotal">
                {(calc?.subtotal ?? subtotal).toFixed(2)} CHF
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">
                {calc?.shipping_label || "Frais d'envoi"}
              </span>
              <span data-testid="cart-shipping">
                {calcLoading
                  ? "…"
                  : calc
                  ? `${calc.shipping_cost.toFixed(2)} CHF`
                  : "—"}
              </span>
            </div>
          </div>

          <div className="border-t border-white/10 mt-4 pt-4 flex justify-between font-display text-xl">
            <span>Total</span>
            <span className="text-magenta" data-testid="cart-total">
              {calc ? calc.total.toFixed(2) : subtotal.toFixed(2)} CHF
            </span>
          </div>

          <button
            onClick={() => navigate("/checkout")}
            disabled={!calc || calcLoading}
            className="btn-fluo w-full mt-6"
            data-testid="checkout-btn"
          >
            Passer commande <ArrowRight size={16} />
          </button>

          <p className="text-xs text-white/40 mt-4 leading-relaxed">
            Pas besoin de créer un compte. Tu renseignes ton adresse de livraison à
            l'étape suivante et tu payes en sécurité avec Stripe.
          </p>
        </aside>
      </div>
    </div>
  );
}
